import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import PageHero from "../components/PageHero";
import StoryCard from "../components/StoryCard";
import useReveal from "../hooks/useReveal";
import { CATEGORIES } from "../mock";
import { fetchStories } from "../lib/api";

const PAGE = 12;

export default function Stories() {
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [visible, setVisible] = useState(PAGE);

  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchStories()
      .then((d) => active && setAll(d))
      .catch(() => active && setError("Could not load stories. Please try again later."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const list = all.filter(
    (s) =>
      (cat === "All" || s.categories.includes(cat)) &&
      (q.trim() === "" || `${s.title} ${s.excerpt}`.toLowerCase().includes(q.toLowerCase()))
  );

  useEffect(() => setVisible(PAGE), [cat, q]);
  const shown = list.slice(0, visible);

  useReveal([shown.length, cat, loading]);

  return (
    <>
      <PageHero
        eyebrow="Start Reading"
        title="Discover Stories"
        subtitle="Real, positive stories of individuals, communities and organizations making a difference in environmental conservation across Puerto Rico and beyond."
      />

      <section className="bg-white">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 py-16">
          <div className="flex flex-col lg:flex-row gap-6 lg:items-center justify-between mb-12">
            <div className="flex flex-wrap gap-2">
              {["All", ...CATEGORIES].map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                    cat === c ? "bg-[#4BAF47] border-[#4BAF47] text-white" : "border-[#e4e2d6] text-[#24231D] hover:border-[#4BAF47] hover:text-[#4BAF47]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="relative lg:w-80">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#878680]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search stories..."
                className="w-full h-12 pl-11 pr-4 rounded-md border border-[#e4e2d6] bg-[#F8F7F0] outline-none focus:ring-2 focus:ring-[#4BAF47] transition-shadow"
              />
            </div>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="stories-loading">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-lg overflow-hidden border border-[#ebe9df]">
                  <div className="aspect-[16/10] bg-[#F8F7F0] animate-pulse" />
                  <div className="p-7 space-y-3">
                    <div className="h-3 w-1/3 bg-[#F8F7F0] rounded animate-pulse" />
                    <div className="h-5 w-3/4 bg-[#F8F7F0] rounded animate-pulse" />
                    <div className="h-3 w-full bg-[#F8F7F0] rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-24 text-[#878680]"><p className="text-xl font-bold text-[#24231D]">{error}</p></div>
          ) : list.length === 0 ? (
            <div className="text-center py-24 text-[#878680]">
              <p className="text-xl font-bold text-[#24231D]">No stories found</p>
              <p className="mt-2">Try another category or search term.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-[#878680] mb-6" data-testid="stories-count">
                Showing {shown.length} of {list.length} {list.length === 1 ? "story" : "stories"}
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {shown.map((s, i) => (
                  <div key={s.id} className="reveal" style={{ transitionDelay: `${(i % 3) * 80}ms` }}>
                    <StoryCard story={s} />
                  </div>
                ))}
              </div>
              {visible < list.length && (
                <div className="text-center mt-14">
                  <button onClick={() => setVisible((v) => v + PAGE)} className="btn-outline" data-testid="load-more">
                    Load More Stories
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}
